import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { CreateScenarioPayload } from '../types/consensus.types';

const SCENARIO_TYPES = [
  'optimistic',
  'conservative',
  'promotion',
  'supply_constraint',
  'custom',
] as const;

interface Props {
  saving?: boolean;
  onSubmit: (payload: CreateScenarioPayload) => Promise<void>;
  onCancel: () => void;
}

export function CreateScenarioForm({ saving, onSubmit, onCancel }: Props) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: '',
    scenario_type: 'optimistic' as CreateScenarioPayload['scenario_type'],
    description: '',
    multiplier: '1.15',
  });

  const submit = async () => {
    if (!form.name.trim()) return;
    const multiplier = Number.parseFloat(form.multiplier);
    await onSubmit({
      name: form.name.trim(),
      scenario_type: form.scenario_type,
      description: form.description.trim(),
      parameters_json: {
        multiplier: Number.isFinite(multiplier) ? multiplier : 1,
      },
    });
  };

  return (
    <div className="border-t border-gray-200 bg-gray-50 px-4 py-4">
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-600">
        {t('consensus.newScenarioFormTitle')}
      </h3>
      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
        <label className="flex flex-col gap-1 text-xs text-gray-600 md:col-span-2">
          {t('consensus.scenarioName')}
          <input
            className="rounded-md border border-gray-300 px-3 py-2 text-sm"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-gray-600">
          {t('sopScenarios.scenarioType')}
          <select
            className="rounded-md border border-gray-300 px-3 py-2 text-sm"
            value={form.scenario_type}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                scenario_type: e.target.value as CreateScenarioPayload['scenario_type'],
              }))
            }
          >
            {SCENARIO_TYPES.map((type) => (
              <option key={type} value={type}>
                {t(`consensus.scenarioTypes.${type}`, type)}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-gray-600">
          {t('consensus.scenarioMultiplier')}
          <input
            type="number"
            step={0.01}
            min={0}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm"
            value={form.multiplier}
            onChange={(e) => setForm((f) => ({ ...f, multiplier: e.target.value }))}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-gray-600 md:col-span-4">
          {t('consensus.comments')}
          <input
            className="rounded-md border border-gray-300 px-3 py-2 text-sm"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          />
        </label>
      </div>
      <p className="mt-2 text-[11px] text-gray-500">{t('consensus.scenarioMultiplierHint')}</p>
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          className="rounded-md bg-stocker-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          disabled={saving}
          onClick={() => void submit()}
        >
          {t('common.save')}
        </button>
        <button
          type="button"
          className="rounded-md border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50 disabled:opacity-50"
          disabled={saving}
          onClick={onCancel}
        >
          {t('common.cancel')}
        </button>
      </div>
    </div>
  );
}
